import type {
  BookingProposalItemRecord,
  BookingProposalItemRecordList,
  BookingProposalRecord,
  BookingProposalRecordList
} from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  catalogPriceMapKey,
  matchProposalCatalogPrices,
  proposalCatalogPricesFromExternalRecords,
  type ProposalCatalogPrice
} from 'noddi-util';
import { useMemo } from 'react';

/** Detail or list proposal shape — prefers nsdk `externalPrices` when the slug API filled them. */
type ProposalCatalogSource = {
  bookingProposalItems: Array<BookingProposalItemRecord | BookingProposalItemRecordList>;
  serviceDepartment: { id: number };
  userGroupMember?: BookingProposalRecord['userGroupMember'];
  userGroup?: BookingProposalRecordList['userGroup'];
  externalPrices?: BookingProposalRecord['externalPrices'];
};

const proposalSalesItemLines = (items: Array<BookingProposalItemRecord | BookingProposalItemRecordList>) =>
  items.flatMap((item) =>
    item.salesItemsWithQuantity.map(({ salesItem }) => ({
      bookingProposalItemId: item.id,
      salesItemId: salesItem.id,
      salesItemName: salesItem.name,
      licensePlateNumber: item.car.licensePlate.number
    }))
  );

/**
 * Customer-facing catalog discounts for proposal lines (EonTyre price lists).
 * Prefers `externalPrices` from the public slug API when present; otherwise
 * resolves the same match from stationary bookable services (plate + dept).
 */
export function useProposalCatalogPrices(proposal: ProposalCatalogSource | undefined) {
  const lines = useMemo(
    () => proposalSalesItemLines(proposal?.bookingProposalItems ?? []),
    [proposal?.bookingProposalItems]
  );
  const serviceDepartmentId = proposal?.serviceDepartment.id;
  const userGroupId = proposal?.userGroupMember?.userGroup.id ?? proposal?.userGroup?.id;

  const externalPrices = proposal?.externalPrices;
  const hasExternalPrices = (externalPrices?.length ?? 0) > 0;

  const uniquePlates = useMemo(() => {
    const seen = new Set<string>();
    const plates: string[] = [];
    for (const line of lines) {
      if (seen.has(line.licensePlateNumber)) {
        continue;
      }
      seen.add(line.licensePlateNumber);
      plates.push(line.licensePlateNumber);
    }
    return plates;
  }, [lines]);

  const bookableQueries = noddiAsync.useGetAll(
    uniquePlates.map((licensePlateNumber) => ({
      type: URLKeys.getStationaryBookableServices,
      input: {
        serviceDepartmentId: serviceDepartmentId!,
        licensePlateNumber,
        userGroupId
      },
      queryConfig: {
        enabled: !!proposal && !hasExternalPrices && serviceDepartmentId != null && !!licensePlateNumber
      }
    }))
  );

  const isPending = !hasExternalPrices && bookableQueries.some((query) => query.isPending);
  const bookableDataUpdatedAt = bookableQueries.map((query) => query.dataUpdatedAt).join(',');

  const prices: ProposalCatalogPrice[] = useMemo(() => {
    if (!proposal) {
      return [];
    }
    if (hasExternalPrices && externalPrices) {
      return proposalCatalogPricesFromExternalRecords({ externalPrices, lines });
    }

    return uniquePlates.flatMap((plate, index) => {
      const data = bookableQueries[index]?.data;
      if (!data) {
        return [];
      }
      return matchProposalCatalogPrices({
        lines: lines.filter((line) => line.licensePlateNumber === plate),
        bookableServices: [...data.services, ...data.addonServices]
      });
    });
    // bookableDataUpdatedAt stands in for query payloads (useQueries array identity churns).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [proposal, hasExternalPrices, externalPrices, lines, uniquePlates, bookableDataUpdatedAt]);

  const priceByCartKey = useMemo(() => {
    const map = new Map<string, ProposalCatalogPrice>();
    for (const price of prices) {
      map.set(catalogPriceMapKey(price.licensePlateNumber, price.salesItemId), price);
    }
    return map;
  }, [prices]);

  return { prices, priceByCartKey, isPending };
}
